function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Gallery Module: Feature Slider
// ----------------------------------------------------------------------------
// The FeatureSlider class manages the product image gallery as a slider
// - It synchronizing the main feature slider with the thumbnail slider (if present)
// - It updates the displayed image based on variant selection and user interactions.
// - Depends on Swiper (swiperjs.com) for the slider functionality.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:gallery:init'    → Listens for the gallery initalization to begin
//                                   initalizing the feature and thumbnail sliders
// - 'productForm:variant:changed' → Updates the displayed image when a different
//                                   variant is selected.
// ----------------------------------------------------------------------------
// Event Triggers:
// - 'productForm:gallery:featureSlider:changed' → Fires when the main feature
//                                                 slider changes slides.
// - 'productForm:gallery:featureSlider:ready'   → Fires when the feature slider
//                                                 is fully initialized.
// ----------------------------------------------------------------------------
// Gotchas:
// - The feature slider initializes *after* the thumbnail slider to ensure proper linking.
// - The focus state of interactive elements (e.g., buttons, iframes, inputs) inside slides
//   is toggled dynamically for accessibility.
// - The variant change handler prioritizes matching the most specific available variant
//   based on selected options (e.g., Color+Waist+Length). If no exact match is found,
//   it will attempt to find a match based on only Option1+Option2. If a match still isn't
//   found, it will attempt matching only Option1. Otherwise, it defaults to the first slide.
// - Consider the image selection logic when defining the option hirearchy accordingly,
//   otherwise override the `focuSlideOnNearestMatch` method to customize the behavior.
// - The thumbnail slider uses a different layout (horizontal by default, vertical for wider screens).
// - If the expected thumbnail element isn't found, the slide change logic may not function correctly.
// - Once the feature slider is inialited, the Media Gallery loading state is disabled.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import Swiper from 'swiper';
import { Navigation, Thumbs } from 'swiper/modules';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { toggleInnerFocus } from 'scripts/helpers/utilities';
import Debug from 'scripts/libs/debug';

// Styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';

// -------------------------------------
// Feature Slider
// -------------------------------------

class FeatureSlider {
  constructor(iamProductGallery) {
    _defineProperty(this, "FOCUSABLE_ELEMENTS", ['iframe', 'input', 'select', 'option', 'textarea', 'button', 'video', 'label', 'a', '.plyr', 'model-viewer']);
    // Reference to parent
    this.iamProductGallery = iamProductGallery;

    // Register Events
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamProductGallery.iamForm,
      source: "featureSlider.js"
    });

    // Initialize Gallery Sliders
    this.thumbnailSlider = this.initThumbnailSlider();
    this.initFeatureSlider(this.thumbnailSlider); // init after thumbnails so they can be linked);

    this.onThumbnailFocus();
  }

  /**
   * On Slide Change
   */
  onSlideChange(swiper) {
    iamotaEventHandler.trigger('productForm:gallery:featureSlider:changed', {
      swiper
    }, {
      context: this.iamProductGallery
    });
    toggleInnerFocus(swiper, this.FOCUSABLE_ELEMENTS);
  }
  onThumbnailFocus() {
    this.thumbnailSlider.slides.forEach((slide, index) => {
      slide.addEventListener('focus', () => {
        this.featureSlider.slideTo(index);
      });
    });
  }

  /**
   * On Variant Change
   */
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    this.focusSlideOnSelection(variant);
  }

  /**
   * Init the thumbnail slider
   */
  initThumbnailSlider() {
    var SLIDE_W = 55.2;
    var SLIDE_GAP = 4;
    return new Swiper('.carousel--product-thumbnails', {
      modules: [Navigation],
      slidesPerView: 'auto',
      spaceBetween: SLIDE_GAP,
      freeMode: true,
      watchSlidesVisibility: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      navigation: true,
      a11y: false,
      multipleActiveThumbs: false,
      keyboard: {
        enabled: false
      },
      on: {
        // When all thumbnails fit in the container, offset the first slide
        // so the group appears centered rather than left-aligned.
        init(s) {
          var count = s.slides.length;
          var totalW = count * SLIDE_W + (count - 1) * SLIDE_GAP;
          var available = s.el.offsetWidth;
          if (totalW < available) {
            var offset = Math.floor((available - totalW) / 2);
            s.params.slidesOffsetBefore = offset;
            s.params.slidesOffsetAfter = offset;
            s.update();
          }
        }
      }
    });
  }

  /**
   * Init the feature slider
   */
  initFeatureSlider(thumbnailSlider) {
    return new Swiper('.carousel--featured-product', {
      modules: [Navigation, Thumbs],
      spaceBetween: 0,
      updateOnImagesReady: true,
      navigation: {
        nextEl: 'iam-product-gallery .swiper-button-next',
        prevEl: 'iam-product-gallery .swiper-button-prev'
      },
      thumbs: {
        swiper: thumbnailSlider ? thumbnailSlider : null
      },
      on: {
        init: function (swiper) {
          Debug.extreme('productForm:gallery:featureSlider:init', swiper);
          this.featureSlider = swiper;
          this.onFeatureSliderReady(swiper);
          toggleInnerFocus(swiper, this.FOCUSABLE_ELEMENTS);
        }.bind(this),
        slideChange: function (swiper) {
          this.onSlideChange(swiper);
        }.bind(this)
      }
    });
  }
  onFeatureSliderReady(swiper) {
    Debug.extreme('productForm:gallery:onFeatureSliderReady', swiper);

    // Reference to the feature slider
    iamotaEventHandler.trigger('productForm:gallery:featureSlider:ready', {
      swiper
    }, {
      context: this.iamProductGallery
    });

    // Update the slider image to the initial variant
    this.focusSlideOnSelection(this.iamProductGallery.iamForm.getCurrentVariant());

    // Disable loading state
    this.iamProductGallery.setLoading(false);
  }
  focusSlideOnSelection(variant) {
    Debug.extreme('productForm:gallery:focusSlideOnSelection', variant);
    if (variant !== null && variant !== void 0 && variant.featured_media) {
      this.focusSlideOnMedia(variant.featured_media.id);
    } else {
      this.focusSlideOnNearestMatch();
    }
  }
  focusSlideOnMedia(mediaId) {
    Debug.extreme('productForm:gallery:focusSlideOnSelection', mediaId);
    var newImageId = "".concat(this.iamProductGallery.sectionId, "-").concat(mediaId);
    var thumbnail = this.iamProductGallery.querySelector("[data-product-single-thumbnail][data-thumbnail-id='".concat(newImageId, "']"));
    if (thumbnail) {
      var _thumbnail$parentElem;
      var slideIndex = parseInt((_thumbnail$parentElem = thumbnail.parentElement) === null || _thumbnail$parentElem === void 0 ? void 0 : _thumbnail$parentElem.getAttribute('data-index'), 10);
      if (!isNaN(slideIndex)) {
        this.featureSlider.slideTo(slideIndex);
      }
    }
  }
  focusSlideOnNearestMatch() {
    Debug.extreme('productForm:gallery:focusSlideOnNearestMatch');
    var options = this.iamProductGallery.iamForm.getCurrentOptions();

    // Check options from most specific to least specific
    // e.g. Check for Color+Waist+Length, Color+Waist, Color
    for (var i = options.length - 1; i > 0; i--) {
      var partialOptions = options.slice(0, i);
      var closestMatch = this.findVariantWithOptions(partialOptions);
      if (closestMatch && closestMatch.featured_media) {
        //console.log('Found something close...', partialOptions, closestMatch);
        this.focusSlideOnMedia(closestMatch.featured_media.id);
        return;
      }
    }
  }

  /**
   * Find a variant that matches the given options
   * @returns {variantObject}
   */
  findVariantWithOptions(options) {
    return this.iamProductGallery.iamForm.productData.variants.find(variant => options.every((option, index) => variant.options[index] === option.value));
  }
}
var featureSliders = [];
iamotaEventHandler.subscribe('productForm:gallery:init', _ref2 => {
  var {
    iamProductGallery
  } = _ref2;
  featureSliders.push(new FeatureSlider(iamProductGallery));
}, {
  source: "featureSlider.js"
});
export default featureSliders;